import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import {
  AdequacyPersonSnapshot,
  AdequacyProfileSnapshot,
  AdequacyReviewDao,
  AdequacyReviewDto,
} from '@app/dao/adequacy/adequacy-review.dao';
import { NotificationsService } from '@app/components/notification/NotificationsService';

/** Une campagne sollicitée dont le serveur attend encore la réponse. */
const isAwaitingClient = (review: AdequacyReviewDto): boolean =>
  review.status === 'PENDING_CLIENT' || review.status === 'REMINDED';

/** Réponse du client, transmise au dashboard. */
export type AdequacyReviewOutcome = 'CONFIRMED' | 'CHANGE_DECLARED';

/**
 * Écran d'évaluation périodique de l'adéquation : présente au client la photo de son profil, puis
 * recueille sa réponse — confirmation ou déclaration de changement.
 */
@Component({
  selector: 'ea-modal-adequacy-review',
  templateUrl: './modal-adequacy-review.component.html',
  styleUrls: ['./modal-adequacy-review.component.scss'],
  standalone: false,
})
export class ModalAdequacyReviewComponent {
  /** Émis une fois la réponse enregistrée côté serveur. */
  @Output() answered = new EventEmitter<AdequacyReviewOutcome>();

  /** Émis quand le client referme sans répondre. */
  @Output() dismissed = new EventEmitter<void>();

  /**
   * La campagne, déjà lue par le tableau de bord : c'est elle qui lui a fait ouvrir cet écran. La
   * relire ici referait le même appel, avec la même photo, à chaque affichage.
   */
  @Input() review: AdequacyReviewDto | null = null;

  submitting = false;

  /** Le client a demandé à déclarer une évolution et doit encore confirmer ce choix définitif. */
  confirmingChange = false;

  constructor(
    private adequacyReviewDao: AdequacyReviewDao,
    private notificationsService: NotificationsService,
  ) {}

  get snapshot(): AdequacyProfileSnapshot | undefined {
    return this.review?.snapshotBefore;
  }

  get hasLegalEntities(): boolean {
    return (this.snapshot?.legalEntities?.length ?? 0) > 0;
  }

  /** Les personnes physiques du foyer. */
  get persons() {
    return this.snapshot?.household?.persons ?? [];
  }

  /**
   * Titre de la photo d'une personne, du point de vue de celle qui consulte. Le rôle dans le foyer
   * ne suffit pas : il ferait lire « Vous » au conjoint sur les valeurs du titulaire, et lui ferait
   * attester d'une situation qui n'est pas la sienne. Sans personne désignée, le titre reste neutre.
   */
  labelFor(person: AdequacyPersonSnapshot): string {
    const viewer = this.review?.viewerOwnerId;
    if (viewer === undefined || viewer === null) {
      return person.role === 'SPOUSE' ? 'Conjoint' : 'Titulaire';
    }
    return person.ownerId === viewer ? 'Vous' : 'Votre conjoint';
  }

  confirmUnchanged(): void {
    this.submit(
      () => this.adequacyReviewDao.confirmUnchanged(this.review!.id),
      'CONFIRMED',
    );
  }

  requestDeclareChange(): void {
    this.confirmingChange = true;
  }

  cancelDeclareChange(): void {
    this.confirmingChange = false;
  }

  declareChange(): void {
    this.submit(
      () => this.adequacyReviewDao.declareChange(this.review!.id),
      'CHANGE_DECLARED',
    );
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  /** Sans effet pendant l'envoi : l'issue de la réponse doit encore parvenir au tableau de bord. */
  close(): void {
    if (this.submitting) {
      return;
    }
    this.dismissed.emit();
  }

  /**
   * Chacun atteste pour lui-même : tant qu'un membre du foyer n'a pas répondu, la campagne reste
   * ouverte et le rapport ne peut pas être établi.
   */
  private confirmationNotice(review: AdequacyReviewDto | null): string {
    return review?.awaitingHousehold
      ? 'Nous attendons encore la réponse des autres membres de votre foyer pour clore votre évaluation.'
      : 'Votre conseiller va établir votre rapport périodique d\'adéquation.';
  }

  /** Envoie la réponse. Ignore les appels concurrents tant qu'une soumission est en cours. */
  private submit(
    call: () => ReturnType<AdequacyReviewDao['confirmUnchanged']>,
    outcome: AdequacyReviewOutcome,
  ): void {
    if (!this.review || this.submitting) {
      return;
    }
    this.submitting = true;

    call().subscribe({
      next: review => {
        this.submitting = false;
        if (!review.answeredByMe && !isAwaitingClient(review)) {
          this.notificationsService.info(
            'Cette évaluation est terminée',
            "Votre réponse n'a pas été enregistrée : votre conseiller reviendra vers vous.",
          );
          this.dismissed.emit();
          return;
        }
        if (outcome === 'CONFIRMED') {
          // « Ma situation a évolué » enchaîne sur le funnel : le client voit qu'il se passe
          // quelque chose. La confirmation, elle, ferme la modale sans autre signe.
          this.notificationsService.success(
            'Merci, votre réponse est enregistrée',
            this.confirmationNotice(review),
          );
        }
        this.answered.emit(outcome);
      },
      error: (error: { status?: number }) => {
        this.submitting = false;
        if (error?.status === 409) {
          this.notificationsService.info(
            'Vous avez déjà déclaré une évolution de votre situation',
            'Votre conseiller reviendra vers vous pour la mettre à jour.',
          );
          this.dismissed.emit();
          return;
        }
        this.notificationsService.error(
          "Votre réponse n'a pas pu être enregistrée. Merci de réessayer dans un instant.",
        );
      },
    });
  }
}
